/**
 * Class create/edit form — used in SCR-010 (list) and SCR-011 (detail).
 * Zod schema mirrors createClassSchema / updateClassSchema from @shared.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const classStatus = z.enum(['planned', 'open', 'running', 'finished', 'cancelled']);

export const classFormSchema = z
  .object({
    code: z.string().min(1, 'Bắt buộc').max(50),
    name: z.string().min(1, 'Bắt buộc').max(200),
    courseId: z.string().min(1, 'Bắt buộc'),
    branchId: z.string().min(1, 'Bắt buộc').optional().or(z.literal('')),
    roomId: z.string().optional().or(z.literal('')),
    status: classStatus.default('planned'),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Định dạng YYYY-MM-DD'),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Định dạng YYYY-MM-DD')
      .optional()
      .or(z.literal('')),
    capacity: z.coerce.number().int().min(1).max(100),
    note: z.string().max(500).optional().or(z.literal('')),
  })
  .refine(
    (d) => {
      if (d.endDate && d.endDate !== '' && d.startDate) {
        return d.endDate >= d.startDate;
      }
      return true;
    },
    { message: 'Ngày bế giảng phải ≥ ngày khai giảng', path: ['endDate'] },
  );

export type ClassFormValues = z.infer<typeof classFormSchema>;

interface CourseOption {
  id: string;
  code: string;
  name: string;
  level?: string;
}

interface BranchOption {
  id: string;
  name: string;
}

interface RoomOption {
  id: string;
  name: string;
}

interface Props {
  isEdit: boolean;
  isSubmitting: boolean;
  defaultValues?: Partial<ClassFormValues>;
  branches?: BranchOption[];
  courses?: CourseOption[];
  rooms?: RoomOption[];
  formError?: string | null;
  onSubmit: (values: ClassFormValues) => void;
  onCancel: () => void;
}

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Chờ khai giảng' },
  { value: 'open', label: 'Mở ghi danh' },
  { value: 'running', label: 'Đang học' },
  { value: 'finished', label: 'Đã kết thúc' },
  { value: 'cancelled', label: 'Đã hủy' },
];

export function ClassForm({
  isEdit,
  isSubmitting,
  defaultValues,
  branches,
  courses = [],
  rooms = [],
  formError,
  onSubmit,
  onCancel,
}: Props) {
  const form = useForm<ClassFormValues>({
    resolver: zodResolver(classFormSchema),
    defaultValues: {
      code: '',
      name: '',
      courseId: '',
      branchId: '',
      roomId: '',
      status: 'planned',
      startDate: '',
      endDate: '',
      capacity: 20,
      note: '',
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        {/* Code — disabled on edit */}
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mã lớp <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={isEdit}
                  placeholder="VD: ENG-A1-2024-Q1"
                  className="font-mono"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tên lớp <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input {...field} placeholder="Tên lớp học" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Course select */}
        <FormField
          control={form.control}
          name="courseId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Khóa học <span className="text-destructive">*</span></FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn khóa học" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {courses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.code} — {c.name}
                      {c.level ? ` (${c.level.toUpperCase()})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Branch — admin only */}
        {branches && (
          <FormField
            control={form.control}
            name="branchId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cơ sở <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn cơ sở" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Room */}
        {rooms.length > 0 && (
          <FormField
            control={form.control}
            name="roomId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phòng mặc định</FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                  value={field.value ? field.value : '__none__'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Không chọn phòng" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__none__">Không chọn</SelectItem>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Status + Capacity in 2-col grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Trạng thái <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="capacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sĩ số tối đa <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="number" min={1} max={100} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Start + End dates */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ngày khai giảng <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ngày bế giảng</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ghi chú</FormLabel>
              <FormControl>
                <Textarea rows={2} maxLength={500} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Đang lưu...' : isEdit ? 'Lưu thay đổi' : 'Tạo lớp'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
